import { useCreateInvoicingAddress, useCreateInvoicingAddressForUser, useGetInvoicingAddressesByUserId, useGetInvoicingAddressesList } from 'dataQueries/invoicingAddresses.query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Big from 'big.js'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { InvoicingAddressFields } from 'components/forms/InvoicingAddress'
import { Country } from 'constants/country'
import constate from 'constate'
import { useRecalculatePrices } from 'dataQueries/purchase.query'
import { FeeDTO } from 'models/fee'
import { useTranslation } from 'react-i18next'
import { useAuth0 } from 'utils/auth'
import { logger } from 'utils/logging'
import { SYNTHETIC_PRODUCT_ID_THRESHOLD } from 'constants/purchaseFlow'
import { useEmbedConfig } from './EmbedConfig.context'
import { useProductAndInstructionLists } from './ProductAndInstructionLists.context'
import { usePurchaseFlowConfig } from './PurchaseFlowConfig.context'
import { usePurchaseFlowProducts } from './PurchaseFlowProducts.context'
import { useTargetOrderUser } from './TargetOrderUser.context'

/**
 * Context provider and hook for managing invoicing addresses within the
 * Purchase Flow (PF) context.
 *
 * @remarks
 * This context is responsible for:
 * - Fetching invoicing addresses for the current user or a target user
 * - Normalizing address queries based on user role (client vs admin)
 * - Managing the selected invoicing address
 * - Handling creation and automatic selection of new invoicing addresses
 */
export const [PFInvoicingAddressesContextProvider, usePFInvoicingAddresses] = constate(() => {
  const { isEmbed } = useEmbedConfig()

  /** Authentication and authorization information of the current user */
  const { roles } = useAuth0()

  /** Target user used when an admin is ordering on behalf of another user */
  const { targetUser, adminSelectedUserEmail } = useTargetOrderUser()

  /** Translation helper scoped to the invoicing address purchase flow */
  const { t } = useTranslation(['invoicing_address_pf'])

  /** Snackbar service used to display success feedback */
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const { sessionId, setVatRateOverride, setDiscountOverride } = usePurchaseFlowConfig()
  const { updateProductPrices } = usePurchaseFlowProducts()
  const { getProductQuantityList, getInstructionQuantityList } = useProductAndInstructionLists()

  /**
   * Query fetching invoicing addresses belonging to the currently authenticated client user.
   *
   * @remarks
   * Enabled only when the user has a client role.
   */
  const invoicingAddressListQuery = useGetInvoicingAddressesList(roles.isClient)

  /**
   * Query fetching invoicing addresses for a target user.
   *
   * @remarks
   * Used when an admin is acting on behalf of another user.
   * Enabled only when the user has an admin role.
   */
  const targetUserAddressListQuery = useGetInvoicingAddressesByUserId(
    targetUser.id,
    roles.isAdmin && !!adminSelectedUserEmail
  )

  /**
   * Mutation used to create a new invoicing address
   * for the currently authenticated user.
   */
  const createInvoicingAddressMutation = useCreateInvoicingAddress()

  /**
   * Mutation used to create a new invoicing address
   * for a specific user (admin flow).
   */
  const createInvoicingAddressForUserMutation = useCreateInvoicingAddressForUser()

  /**
   * Normalizes the active invoicing address query based on user role.
   *
   * @remarks
   * - Clients use their own invoicing address list
   * - Admins use the target user's invoicing address list
   */
  const normalizedListQuery = useMemo(() => {
    if (roles.isAdmin) {
      return targetUserAddressListQuery
    }

    return invoicingAddressListQuery
  }, [invoicingAddressListQuery, roles.isAdmin, targetUserAddressListQuery])

  /**
   * Normalized list of invoicing addresses.
   *
   * @remarks
   * Always returns an array.
   * Falls back to an empty array while the query is loading or unavailable.
   */
  const invoicingAddressList = useMemo(
    () => normalizedListQuery.data ?? [],
    [normalizedListQuery.data]
  )

  /**
   * Currently selected invoicing address.
   *
   * @remarks
   * Possible values:
   * - string: ID of an existing invoicing address
   * - 'new': indicates the "create new address" flow
   * - undefined: no address selected yet
   */
  const [selectedInvoicingAddress, setSelectedInvoicingAddress] =
    useState<string | 'new' | undefined>()

  // Clear any stale selection when the target user changes (admin switching clients)
  useEffect(() => {
    setSelectedInvoicingAddress(undefined)
  }, [targetUser.id])

  // Auto-select the invoicing address when only one exists
  useEffect(() => {
    if (normalizedListQuery.isSuccess && invoicingAddressList.length === 1 && !selectedInvoicingAddress) {
      setSelectedInvoicingAddress(invoicingAddressList[0].id)
    }
  }, [normalizedListQuery.isSuccess, invoicingAddressList, selectedInvoicingAddress])

  /**
   * Creates a new invoicing address for the current user
   * and automatically selects it on success.
   *
   * @param values - Invoicing address form values
   *
   * @remarks
   * On success:
   * - Updates the selected address to the newly created address ID
   * - Displays a success toast notification
   */
  const saveAndUseInvoicingAddress = useCallback((values: InvoicingAddressFields) => {
    createInvoicingAddressMutation.mutate(
      {
        addressName: values.addressName,
        companyOrIndividualName: values.companyOrIndividualName,
        streetAndNumber: values.streetAndNumber,
        apartmentNumber: values.apartmentNumber,
        city: values.city,
        region: values.region,
        country: values.country as Country,
        postalCode: values.postalCode,
        vatNumber: values.vatNumber?.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          setSelectedInvoicingAddress(data.id)
          spawnSuccessToast(t('create_success'))
        },
        onError: (error) => {
          // W2.5 (B-17): add alert-level feedback so the user knows the address
          // save failed even if their focus has moved away from the card.
          // The card-level QueryStatus already shows inline error state, but
          // that requires visual attention — the toast ensures the signal
          // reaches the user regardless of scroll position.
          spawnErrorToast(t('create_error'))
          logger.error('Failed to create invoicing address', error, {
            context: { component: 'InvoicingAddress', action: 'saveAndUseInvoicingAddress' },
          })
        },
      }
    )
  }, [createInvoicingAddressMutation, spawnErrorToast, spawnSuccessToast, t])

  /**
   * Creates a new invoicing address for a specific user
   * and automatically selects it on success.
   *
   * @param userId - Identifier of the user for whom the address is created
   * @param values - Invoicing address form values
   *
   * @remarks
   * Intended for admin flows where an address is created
   * on behalf of another user.
   *
   * On success:
   * - Updates the selected address to the newly created address ID
   * - Displays a success toast notification
   */
  const saveAndUseInvoicingAddressForUser = useCallback((userId: string, values: InvoicingAddressFields) => {
    createInvoicingAddressForUserMutation.mutate(
      {
        userId,
        addressName: values.addressName,
        companyOrIndividualName: values.companyOrIndividualName,
        streetAndNumber: values.streetAndNumber,
        apartmentNumber: values.apartmentNumber,
        city: values.city,
        region: values.region,
        country: values.country as Country,
        postalCode: values.postalCode,
        vatNumber: values.vatNumber?.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          setSelectedInvoicingAddress(data.id)
          spawnSuccessToast(t('create_success'))
        },
        onError: (error) => {
          // W2.5 (B-17): mirror the client path — toast on error for admin flow too.
          spawnErrorToast(t('create_error'))
          logger.error('Failed to create invoicing address for user', error, {
            context: { component: 'InvoicingAddress', action: 'saveAndUseInvoicingAddressForUser' },
          })
        },
      }
    )
  }, [createInvoicingAddressForUserMutation, spawnErrorToast, spawnSuccessToast, t])

  // -- RECALCULATE PRICES ON INVOICING ADDRESS CHANGE --

  const recalculatePricesMutation = useRecalculatePrices()

  // Tracks the latest address that triggered a recalculation to discard stale responses
  const latestRecalcAddressRef = useRef<string | undefined>()

  // Reactive snapshot of the products + chargeable instructions sent to the recalc endpoint.
  // The underlying getters are memoized on the cart state, so this list's identity changes
  // whenever the cart changes — which lets the recalc effect re-run once products load
  // (the address can auto-select before the cart is populated for single-address clients).
  const recalcProductList = useMemo(
    () => [
      ...getProductQuantityList(),
      ...getInstructionQuantityList().filter((product) => product.id < SYNTHETIC_PRODUCT_ID_THRESHOLD),
    ],
    [getProductQuantityList, getInstructionQuantityList]
  )

  // Stable content signature used as the recalc effect dependency, so it fires on real cart
  // changes (id/count/option) without re-firing on unrelated reference churn (e.g. i18n `t`).
  const recalcProductListKey = useMemo(
    () => recalcProductList.map((product) => `${product.id}:${product.count}:${product.isOption ? 1 : 0}`).join(','),
    [recalcProductList]
  )

  // Reset mutation state when session changes (e.g. category/address change)
  useEffect(() => {
    recalculatePricesMutation.reset()
    // recalculatePricesMutation is excluded from deps because .reset() changes its
    // reference identity, which would cause an infinite re-render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  useEffect(() => {
    // In the Propstack embed the catalogue prices are channel-aware (revenueChannel/isOneSystem —
    // OneSystem quota lines at 0 € with a struck-through public price). The recalculate-prices
    // endpoint knows nothing about revenue channels, so its response would overwrite the covered
    // lines with full public prices (ISSUE-2373). The embed reprice lives in the Product step
    // (channel-aware catalogue re-fetch); VAT/discount stay on the catalogue values.
    if (isEmbed) return
    if (!sessionId) return

    // Reset overrides when no valid address is selected
    if (!selectedInvoicingAddress || selectedInvoicingAddress === 'new') {
      setVatRateOverride(null)
      setDiscountOverride(null)
      return
    }

    // Guard: skip if no products selected
    if (recalcProductList.length === 0) return

    const addressAtCallTime = selectedInvoicingAddress
    latestRecalcAddressRef.current = addressAtCallTime

    recalculatePricesMutation.reset()
    recalculatePricesMutation.mutate(
      {
        sessionId,
        products: recalcProductList,
        invoicingAddressId: selectedInvoicingAddress,
        ...(roles.isAdmin && adminSelectedUserEmail ? { email: adminSelectedUserEmail } : {}),
      },
      {
        onSuccess: (response) => {
          // Discard stale response if the user switched to a different address
          if (latestRecalcAddressRef.current !== addressAtCallTime) return

          const data = response.data.data

          // Update VAT and discount overrides
          setVatRateOverride(Big(data.vatRate))
          setDiscountOverride(Big(data.discount))

          // Update product prices
          const priceMap = new Map<number, FeeDTO>()
          for (const product of data.products) {
            priceMap.set(product.id, product.feePrice)
          }
          updateProductPrices(priceMap)
        },
        onError: (error) => {
          // Discard stale error if the user switched to a different address
          if (latestRecalcAddressRef.current !== addressAtCallTime) return

          logger.error('Failed to recalculate prices', error, {
            context: { component: 'InvoicingAddress', action: 'recalculatePrices' },
          })
          spawnErrorToast(t('recalculate_error'))
        },
      }
    )

    // Trigger on address, session, or cart change. `recalcProductListKey` carries the cart
    // reactivity (the `recalcProductList` reference is read inside but changes in lockstep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoicingAddress, sessionId, isEmbed, recalcProductListKey])

  // -- END OF RECALCULATE PRICES --

  return {
    /** Active invoicing address query resolved based on user role */
    normalizedListQuery,

    /** Normalized list of invoicing addresses (never undefined) */
    invoicingAddressList,

    /** Mutation for creating an invoicing address for the current user */
    createInvoicingAddressMutation,

    /** Mutation for creating an invoicing address for a target user */
    createInvoicingAddressForUserMutation,

    /** Currently selected invoicing address ID or 'new' */
    selectedInvoicingAddress,

    /** Setter for the selected invoicing address */
    setSelectedInvoicingAddress,

    /** Helper to create and immediately select an invoicing address for current user */
    saveAndUseInvoicingAddress,

    /** Helper to create and immediately select an invoicing address for a target user */
    saveAndUseInvoicingAddressForUser,

    /** Mutation for recalculating prices based on invoicing address */
    recalculatePricesMutation,
  }
})
